import { useState, useEffect, useMemo } from 'react';
import { Sparkles, Check } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../hooks/useToast';
import { api } from '../lib/api';
import { formatINR } from '../lib/formatINR';
import ConfirmModal from '../components/ConfirmModal';

function toCardKey(planKey) {
  return planKey === 'free' ? 'basic' : planKey;
}

export default function Billing() {
  const { token, user, planState, refreshPlan } = useAuth();
  const toast = useToast();
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pending, setPending] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.plansPublic()
      .then(r => setPlans(r.plans || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const currentCardKey = useMemo(
    () => toCardKey(planState?.effective_plan ?? planState?.plan ?? 'free'),
    [planState]
  );

  const confirmUpgrade = async () => {
    if (!pending || !token) return;
    if (user?.role !== 'owner') {
      toast.show('Only the workspace owner can change the plan.', 'error');
      setPending(null);
      return;
    }
    setSaving(true);
    try {
      const planKey = pending.key === 'free' ? 'basic' : pending.key;
      await api.billingSimulateUpgrade(token, planKey);
      if (refreshPlan) await refreshPlan();
      toast.show(`You're now on ${pending.display_name}!`, 'success');
      setPending(null);
    } catch (e) {
      toast.show(e?.message || 'Upgrade failed', 'error');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="p-6 lg:p-8 max-w-[900px] mx-auto" data-testid="billing-page">
      <h1 className="text-2xl font-extrabold text-white tracking-tight mb-6" data-testid="billing-title">Billing</h1>

      {planState?.trial_active ? (
        <div className="relative overflow-hidden rounded-3xl p-6 mb-8" data-testid="billing-trial-card"
          style={{ background: 'linear-gradient(135deg, #7C5CFF, #5B3FE5)' }}>
          <div className="absolute inset-0 bg-gradient-to-br from-white/[0.28] via-transparent to-transparent pointer-events-none" />
          <div className="relative z-[2]">
            <div className="w-12 h-12 rounded-full bg-white/20 border border-white/[0.28] flex items-center justify-center mb-3">
              <Sparkles size={20} className="text-white" />
            </div>
            <p className="text-2xl font-extrabold text-white tracking-tight">
              {planState.trial_days_left ?? 0} {(planState.trial_days_left ?? 0) === 1 ? 'day' : 'days'} left
            </p>
            <p className="text-white/90 text-sm mt-1">
              You&apos;re on the 14-Day Premium Trial. Pick a plan below to keep everything after it ends.
            </p>
          </div>
        </div>
      ) : (
        <div className="glass-card p-6 mb-8" data-testid="billing-current-plan">
          <p className="text-xs text-white/50 uppercase font-semibold tracking-wider">Current plan</p>
          <p className="text-2xl font-extrabold text-white mt-1">
            {plans.find(p => p.key === (planState?.plan ?? 'free'))?.display_name ?? 'Basic'}
          </p>
          {planState?.plan && planState.plan !== 'free' ? (
            <p className="text-white/60 text-sm mt-1">
              {formatINR(plans.find(p => p.key === planState.plan)?.price_inr ?? 0)} / month · Renews automatically
            </p>
          ) : (
            <p className="text-white/60 text-sm mt-1">Free forever. Upgrade any time.</p>
          )}
        </div>
      )}

      <h2 className="text-xl font-extrabold text-white text-center mb-1">Choose your plan</h2>
      <p className="text-white/50 text-sm text-center mb-8">All plans include GST invoicing, mobile app, and unlimited customers.</p>

      {loading ? (
        <div className="flex justify-center py-16">
          <div className="w-8 h-8 border-2 border-teal-400 border-t-transparent rounded-full animate-spin" />
        </div>
      ) : (
        <div className="space-y-4" data-testid="billing-plan-list">
          {plans.map(p => {
            const cardKey = toCardKey(p.key);
            const isCurrent = cardKey === currentCardKey;
            const isPro = p.key === 'pro';
            return (
              <div key={p.key} data-testid={`billing-plan-${cardKey}`}
                className={`glass-card p-6 relative ${isPro ? 'ring-2 ring-teal-400/40' : ''}`}>
                {p.highlight && (
                  <span className={`absolute -top-3 right-5 px-3 py-1.5 rounded-full text-[11px] font-extrabold uppercase tracking-wider shadow-lg ${isPro ? 'bg-teal-500 text-white' : 'bg-white/10 text-white/80 border border-white/15'}`}>
                    {p.highlight}
                  </span>
                )}
                <p className="text-xs text-white/50 font-bold uppercase tracking-wider">{p.display_name}</p>
                <div className="flex items-end gap-1 mt-1">
                  <span className="text-3xl font-extrabold text-white tracking-tight" data-testid={`billing-price-${cardKey}`}>
                    {formatINR(p.price_inr)}
                  </span>
                  {p.price_inr > 0 && <span className="text-white/40 text-sm mb-1.5"> / month</span>}
                </div>
                <p className="text-white/60 text-sm mt-1">{p.tagline}</p>
                <div className="mt-4 space-y-2.5 mb-5">
                  {(p.unlocks || []).slice(0, 5).map(u => (
                    <div key={u} className="flex items-start gap-2.5">
                      <div className="w-5 h-5 rounded-full bg-emerald-500/15 flex items-center justify-center flex-shrink-0 mt-0.5">
                        <Check size={12} className="text-emerald-400" />
                      </div>
                      <span className="text-white/80 text-sm">{u}</span>
                    </div>
                  ))}
                </div>
                {isCurrent ? (
                  <div className="flex items-center justify-center gap-2 h-12 rounded-2xl bg-teal-500/10 border border-teal-500/30" data-testid={`billing-cta-current-${cardKey}`}>
                    <Check size={14} className="text-teal-400" />
                    <span className="text-teal-400 font-bold text-sm">Current plan</span>
                  </div>
                ) : (
                  <button data-testid={`billing-cta-simulate-${cardKey}`} onClick={() => setPending(p)}
                    className={`w-full h-12 rounded-2xl font-bold text-sm transition-all ${isPro
                      ? 'bg-gradient-to-r from-cyan-400 to-teal-500 text-white hover:shadow-lg hover:shadow-teal-500/30'
                      : `border ${p.key === 'premium' ? 'border-[#7C5CFF] text-[#7C5CFF]' : 'border-white/15 text-white/80'} hover:bg-white/5`
                    }`}>
                    Simulate Payment
                  </button>
                )}
              </div>
            );
          })}
        </div>
      )}

      <div className="mt-8 p-3 rounded-2xl bg-white/[0.04] border border-white/10 text-center" data-testid="billing-disclosure">
        <p className="text-white/40 text-xs">Payments are simulated for development. Razorpay / Stripe integration coming soon.</p>
      </div>

      <ConfirmModal
        open={!!pending}
        onClose={() => { if (!saving) setPending(null); }}
        onConfirm={confirmUpgrade}
        title="Confirm upgrade"
        message={pending ? `Simulate payment of ${formatINR(pending.price_inr)}/mo for the ${pending.display_name} plan?` : ''}
        confirmText="Confirm"
        variant="confirm"
        loading={saving}
      />
    </div>
  );
}
